import { getCombatVehicleSeats, type VehicleSeat } from "@endclock/shared";
import { loadCharacterGlb, type CharacterAsset } from "../character/character-asset.js";
import { CharacterRenderer } from "../character/character-renderer.js";
import { copyMatrix, eulerQuaternion, multiplyMatrices, writeTrs } from "../character/character-pose.js";
import type { Camera } from "../engine/camera.js";
import { writeUprightTransform } from "../engine/math.js";
import type { Renderer } from "../engine/renderer.js";
import type { MachinePose } from "./model.js";

/** Uses the existing twelve human joints, authored skin and normal character draw path. */
export class VehicleCrewPose {
  readonly palette: Float32Array;
  readonly model = new Float32Array(16);
  readonly translations: Float32Array;
  readonly rotations: Float32Array;
  private readonly scales: Float32Array;
  private readonly local: Float32Array;
  private readonly global: Float32Array;
  readonly position = { x: 0, y: 0, z: 0, yaw: 0 };

  constructor(private readonly asset: CharacterAsset) {
    const count = asset.joints.length;
    this.palette = new Float32Array(count * 16); this.local = new Float32Array(count * 16); this.global = new Float32Array(count * 16);
    this.translations = new Float32Array(count * 3); this.rotations = new Float32Array(count * 4); this.scales = new Float32Array(count * 3);
    for (const [index, joint] of asset.joints.entries()) { this.translations.set(joint.translation, index * 3); this.scales.set(joint.scale, index * 3); }
  }

  update(vehicle: MachinePose, seat: VehicleSeat, now: number): void {
    const gunner = seat.mount.parent === "turret", yaw = gunner ? vehicle.turretYaw : vehicle.yaw;
    const [mx, my, mz] = seat.mount.position, slump = vehicle.collapse;
    const pivotX = vehicle.x - (gunner ? Math.sin(vehicle.yaw) * 1.25 : 0);
    const pivotZ = vehicle.z - (gunner ? Math.cos(vehicle.yaw) * 1.25 : 0);
    const hipRestY = this.asset.joints.find(joint => joint.name === "pelvis")?.translation[1] ?? .91;
    const position = this.position;
    position.x = pivotX + mx * Math.cos(yaw) + mz * Math.sin(yaw);
    position.z = pivotZ - mx * Math.sin(yaw) + mz * Math.cos(yaw);
    position.y = vehicle.y + (gunner ? 2.05 : 0) + my - hipRestY - slump * (gunner ? .18 : .13);
    position.yaw = yaw;
    writeUprightTransform(this.model, 0, position.x, position.y, position.z, yaw, 1);
    for (const [index, joint] of this.asset.joints.entries()) {
      let x = 0, y = 0, z = 0;
      if (joint.name.startsWith("upper_leg")) x = -1.43;
      else if (joint.name.startsWith("lower_leg")) x = 1.53;
      else if (joint.name.startsWith("upper_arm")) { x = -.59 - slump * .31; z = joint.name.endsWith("_l") ? -.13 : .13; }
      else if (joint.name.startsWith("lower_arm")) x = -1.03 + slump * .70;
      else if (joint.name === "spine") x = .055 + slump * .72 + (gunner ? -vehicle.aimPitch * .15 : 0);
      else if (joint.name === "head") { x = slump * .39; y = gunner ? 0 : (vehicle.steering ?? 0) * .12 + Math.sin(now * .0007) * .035; }
      else if (joint.name === "pelvis") x = -.025;
      this.rotations.set(eulerQuaternion(x, y, z), index * 4);
      writeTrs(this.local, index * 16, this.translations, index * 3, this.rotations, index * 4, this.scales, index * 3);
      if (joint.parent < 0) copyMatrix(this.global, index * 16, this.local, index * 16);
      else multiplyMatrices(this.global, index * 16, this.global, joint.parent * 16, this.local, index * 16);
      multiplyMatrices(this.palette, index * 16, this.global, index * 16, this.asset.inverseBindMatrices, index * 16);
    }
  }
}

interface CrewMember { seat: VehicleSeat; pose: VehicleCrewPose }
interface CrewVehicle { epoch: number; members: CrewMember[] }
/** One shared survivor mesh upload, exactly two mounted humans per technical, zero NPC slots. */
export class CombatVehicleCrew {
  private asset: CharacterAsset | undefined;
  private characterRenderer: CharacterRenderer | undefined;
  private readonly vehicles = new Map<number, CrewVehicle>();
  private loading = false; private disposed = false;
  private loadError: string | null = null;
  visible = 0; drawCalls = 0;
  constructor(private readonly renderer: Renderer) {}

  update(id: number, epoch: number, vehicle: MachinePose, now: number): void {
    if (!this.loading && typeof window !== "undefined") {
      this.loading = true;
      // Existing neutral scavenger outfit; same validated rig as the player and public NPCs.
      void loadCharacterGlb("/assets/characters/npc_02.glb?v=immersive-1").then(asset => {
        if (this.disposed) return;
        this.asset = asset; this.characterRenderer = new CharacterRenderer(this.renderer, asset, this.renderer.atmosphereFeatureFlags);
      }).catch((error: unknown) => { this.loadError = error instanceof Error ? error.message : "Crew asset unavailable"; });
    }
    if (!this.asset) return;
    let crew = this.vehicles.get(id);
    if (!crew || crew.epoch !== epoch) {
      crew = { epoch, members: getCombatVehicleSeats(id, epoch).map(seat => ({ seat, pose: new VehicleCrewPose(this.asset!) })) };
      this.vehicles.set(id, crew);
    }
    for (const member of crew.members) member.pose.update(vehicle, member.seat, now);
  }
  retain(actorIds: ReadonlySet<number>): void { for (const id of this.vehicles.keys()) if (!actorIds.has(id)) this.vehicles.delete(id); }
  render(camera: Camera, shadow = false): void {
    const renderer = this.characterRenderer;
    if (!shadow) { this.visible = 0; this.drawCalls = 0; }
    if (!renderer) return;
    const before = this.renderer.metrics.drawCalls;
    if (shadow) renderer.beginShadow(camera.viewProjection); else renderer.begin(camera.viewProjection, camera.position, camera.target);
    for (const crew of this.vehicles.values()) for (const member of crew.members) {
      const p = member.pose.position;
      if (!camera.isSphereVisible(p.x, p.y + 1.3, p.z, 1.1)) continue;
      if (shadow) renderer.drawShadow(member.pose.model, member.pose.palette);
      else { renderer.draw(member.pose.model, member.pose.palette); this.visible++; }
    }
    if (!shadow) this.drawCalls = this.renderer.metrics.drawCalls - before;
  }
  snapshot(id?: number) {
    return { loaded: !!this.asset, error: this.loadError, visible: this.visible, drawCalls: this.drawCalls,
      members: [...this.vehicles.entries()].filter(([vehicleId]) => id === undefined || id === vehicleId).flatMap(([vehicleId, crew]) =>
        crew.members.map(member => ({ vehicleId, epoch: crew.epoch, seat: member.seat, position: { ...member.pose.position } }))) };
  }
  reset(): void { this.vehicles.clear(); this.visible = 0; this.drawCalls = 0; }
  dispose(): void { this.disposed = true; this.reset(); this.characterRenderer?.dispose(); }
}
